
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  provide,
  watchEffect,
} from "vue";
import ImagesUpdate from "@/components/wizard/steps/ImagesUpdate.vue";
import UploadPitchDeckUpdate from "@/components/wizard/steps/UploadPitchDeckUpdate.vue";
import BusinessDescriptionUpdate from "@/components/wizard/steps/BusinessDescriptionUpdate.vue";
import BusinessDetailsUpdate from "@/components/wizard/steps/BusinessDetailsUpdate.vue";
import AdvancedAssuranceUpdate from "@/components/wizard/steps/AdvancedAssuranceUpdate.vue";
import IndustrySectorsBusinessesUpdate from "@/components/wizard/steps/IndustrySectorsBusinessesUpdate.vue";
import TeamMembersUpdate from "@/components/wizard/steps/TeamMembersUpdate.vue";
import { StepperComponent } from "@/assets/ts/components";
import * as Yup from "yup";
import { useForm } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useRoute } from "vue-router";
import { Actions } from "@/store/enums/StoreEnums";

interface IStep1 {
  name: string;
  jobTitle: string;
  telephone: string;
  linkedInProfileUrl: string;
}

interface IStep2 {
  introduction: string;
  bio: string;
}

interface IStep3 {
  linkToExternalDocuments: string;
  documents: unknown;
}

interface IStep4 {
  expertIndustrySectors: Array<number | string>;
  expertExpertise: Array<number | string>;
}

interface CreateAccount extends IStep1, IStep2, IStep3, IStep4 {}

export default defineComponent({
  name: "update-investment-opportunity",
  components: {
    ImagesUpdate,
    UploadPitchDeckUpdate,
    BusinessDescriptionUpdate,
    BusinessDetailsUpdate,
    AdvancedAssuranceUpdate,
    TeamMembersUpdate,
    IndustrySectorsBusinessesUpdate,
  },
  setup() {
    const isSelected = ref<boolean>(false);
    const touchedAdvancedAssurance = ref<boolean>(false);
    const loading = ref<boolean>(false);
    const fileSizeError = ref<boolean>(false);
    const touched = ref<boolean>(false);
    const teamMembersLength = ref<number>(0);
    const industrySectorsLength = ref<number>(0);
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const businesses = computed(() => store.getters.getBusinesses);
    const business = ref<Record<string, unknown>>({});
    const formDataTemp = ref<Record<string, unknown>>({});
    const _stepperObj = ref<StepperComponent | null>(null);
    const verticalWizardRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(0);
    const changeFormData = values => {
      formDataTemp.value = {...values}
    }
    const formData = ref<CreateAccount>({
      name: "",
      jobTitle: "",
      telephone: "",
      linkedInProfileUrl: "",
      introduction: "",
      bio: "",
      linkToExternalDocuments: "",
      documents: null,
      expertIndustrySectors: [],
      expertExpertise: [],
    });
    provide("business", business);
    const syncData = () => {
      const tempFormData = {}
      for (const key in business.value) {
        if (key == "industrySectors" && business.value[key]) {
          tempFormData[key] =
              (business.value[key] as Array<Record<string, unknown>>).map(
                (sector) => sector.id
              )
        } else {
          tempFormData[key] = business.value[key]
        }
      }
      console.log({tempFormData})
      formDataTemp.value = {...tempFormData}
    };
    const touchedIndustrySector = ref<boolean>(false);
    const handleTouch = () => {
      if (_stepperObj.value?.currentStepIndex == 5)
        touchedAdvancedAssurance.value = true;
      if (_stepperObj.value?.currentStepIndex == 6) touched.value = true;
      if (_stepperObj.value?.currentStepIndex == 7)
        touchedIndustrySector.value = true;
    };
    onMounted(async () => {
      _stepperObj.value = StepperComponent.createInsance(
        verticalWizardRef.value as HTMLElement
      );
      if (!businesses.value?.founderBusinesses?.length) {
        try {
          const response = await store.dispatch(
            Actions.GET_FOUNDER_BUSINESSES,
            {
              page: 1,
              perPage: 10,
            }
          );
          if (response !== true) throw new Error();
          business.value = businesses.value?.founderBusinesses.find(
            (b) => b.id == route.params.id
          );
          syncData();
        } catch (err) {
          console.log(err);
        }
      } else {
        business.value = businesses.value?.founderBusinesses.find(
          (b) => b.id == route.params.id
        );
        syncData();
      }
      setCurrentPageBreadcrumbs("Update Investment Opportunity", [
        "Businesses",
      ]);
    });
    const createAccountSchema = computed(() => {
      let tempObj = {};
      for (let i = 0; i < +teamMembersLength.value; i++) {
        tempObj[`team-member-name-${i}`] = Yup.string()
          .required()
          .label("Name");
        tempObj[`team-member-linkedInProfileUrl-${i}`] = Yup.string()
          .required()
          .label("LinkedIn Profile");
        tempObj[`team-member-jobTitle-${i}`] = Yup.string()
          .required()
          .label("Job Title");
        tempObj[`team-member-introduction-${i}`] = Yup.string()
          .required()
          .label("Introduction");
      }
      return [
        {},
        Yup.object({
          pitchDeckDocument: Yup.mixed()
            .required()
            .label("Pitch Deck Document"),
        }),
        Yup.object({
          name: Yup.string().required().label("Name"),
          summary: Yup.string().required().label("Summary"),
          overview: Yup.string().required().label("Overview"),
          defensibleUsp: Yup.string().required().label("Defensible USP"),
        }),
        Yup.object({
          telephone: Yup.string()
            .required()
            .min(12, "Telephone must be 11 characters long.")
            .label("Telephone"),
          website: Yup.string()
            .matches(
              /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
              "Enter correct url!"
            )
            .required()
            .label("Website"),
          currencyCode: Yup.string().required().label("Currency"),
          geoFocusCountryCode: Yup.string().required().label("Geo Focus"),
        }),
        {},
        tempObj,
        {},
        Yup.object({
          fundingRoundName: Yup.string().required().label("Name"),
          fundingRoundInvestmentRequired: Yup.string()
            .required()
            .label("Investment Required"),
          fundingRoundPreMoneyValuation: Yup.string()
            .required()
            .label("Pre-Money Valuation"),
          fundingRoundMinimumInvestment: Yup.string()
            .required()
            .label("Minimum Investment"),
        }),
      ];
    });

    const currentSchema = ref<Record<string, unknown>>({});
    watchEffect(() => {
      currentSchema.value = createAccountSchema.value[currentStepIndex.value];
    });
    const { resetForm, handleSubmit } = useForm<
      IStep1 | IStep2 | IStep3 | IStep4
    >({
      validationSchema: currentSchema,
    });

    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });

    resetForm({
      values: {
        ...formData.value,
      },
    });

    const handleStep = handleSubmit(async (values) => {
      if (!isSelected.value && _stepperObj.value?.currentStepIndex == 5) return;
      if (!teamMembersLength.value && _stepperObj.value?.currentStepIndex == 6)
        return;
      if (
        !industrySectorsLength.value &&
        _stepperObj.value?.currentStepIndex == 7
      )
        return;
      if (fileSizeError.value) return;
      formData.value = {
        ...formData.value,
        ...values,
      };
      try {
        const response = await store.dispatch(Actions.UPDATE_FOUNDER_BUSINESS, {
          id: route.params.id,
          payload: formDataTemp.value,
        });
        if (response !== true) throw new Error();
        currentStepIndex.value++;

        if (!_stepperObj.value) {
          return;
        }

        _stepperObj.value.goNext();
      } catch (err) {
        store.commit("setAlert", {
          message: "Error",
          subMessage: "`Investment Opportunity Updation Unsuccessful`",
          variant: "danger",
          duration: 4000,
          show: true,
        });
      }
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };

    const formSubmit = async () => {
      loading.value = true;
      try {
        const response = await store.dispatch(Actions.UPDATE_FOUNDER_BUSINESS, {
          id: route.params.id,
          payload: formDataTemp.value,
        });
        if (response !== true) throw new Error();
        loading.value = false;
        store.commit("setAlert", {
          message: "Success",
          subMessage: `Investment Opportunity ${"Updated"}`,
          variant: "primary",
          duration: 4000,
          show: true,
        });
        setTimeout(() => {
          router.push("/investment-opportunities");
        }, 2000);
      } catch (err) {
        loading.value = false;
        store.commit("setAlert", {
          message: "Error",
          subMessage: "`Investment Opportunity Updation Unsuccessful`",
          variant: "danger",
          duration: 4000,
          show: true,
        });
      }
    };
    return {
      touchedAdvancedAssurance,
      isSelected,
      touchedIndustrySector,
      industrySectorsLength,
      loading,
      fileSizeError,
      touched,
      teamMembersLength,
      formDataTemp,
      verticalWizardRef,
      previousStep,
      handleStep,
      formSubmit,
      totalSteps,
      currentStepIndex,
      handleTouch,
      changeFormData
    };
  },
});
